import React, { useState, useCallback } from 'react';
import { getFieldType } from '../../../config/customFieldTypes';
import FieldRenderer from './FieldRenderer';
import FieldEditor from './FieldEditor';
import './styles/index.css';

/**
 * CustomFieldsDisplay Component
 * 
 * Displays custom field values on mission cards.
 * Shared between Flow view (TaskNode) and Kanban view (KanbanCard)
 * via the MissionCardBody component.
 * 
 * Each field shows its label and value, with click-to-edit functionality.
 * Visibility of individual fields is controlled via cardDisplaySettings.
 */
const CustomFieldsDisplay = ({
  task,
  customFields = [],
  cardDisplaySettings = {},
  onFieldValueChange,
  classPrefix = 'mission-card',
  compact = false, // Compact mode for smaller cards
}) => {
  const [editingFieldId, setEditingFieldId] = useState(null);

  // Get field values from task
  const fieldValues = task?.customFieldValues || {};

  // Filter to only fields that are visible (enabled via cardDisplaySettings)
  // Custom fields default to hidden (false) until explicitly enabled
  // NOTE: 'select' type fields are rendered as dropdowns in the main dropdown row,
  // so we exclude them here to avoid duplication
  const fieldsToDisplay = customFields
    .filter(field => {
      // Custom fields must be explicitly enabled (default to false/hidden)
      const isVisible = cardDisplaySettings[field.id] === true;
      // Exclude select fields - they're rendered in the dropdown row
      const isNotSelect = field.type !== 'select';
      return isVisible && isNotSelect;
    })
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  // Handle field value change
  const handleValueChange = useCallback((fieldId, newValue) => {
    onFieldValueChange?.(task.id, fieldId, newValue);
    setEditingFieldId(null);
  }, [task?.id, onFieldValueChange]);

  // Handle click to edit
  const handleFieldClick = useCallback((field, e) => {
    e.stopPropagation();
    if (field.type === 'checkbox') {
      // Checkbox toggles directly without editing mode
      const currentValue = fieldValues[field.id];
      onFieldValueChange?.(task.id, field.id, !currentValue);
    } else {
      setEditingFieldId(field.id);
    }
  }, [task?.id, fieldValues, onFieldValueChange]);

  // Handle close editor
  const handleCloseEditor = useCallback(() => {
    setEditingFieldId(null);
  }, []);

  // Don't render if no fields to display
  if (fieldsToDisplay.length === 0) {
    return null;
  }

  return (
    <div className={`${classPrefix}__custom-fields ${compact ? `${classPrefix}__custom-fields--compact` : ''}`}>
      {fieldsToDisplay.map(field => {
        const fieldType = getFieldType(field.type);
        const value = fieldValues[field.id];
        const isEditing = editingFieldId === field.id;

        return (
          <div 
            key={field.id} 
            className={`${classPrefix}__custom-field`}
            onClick={(e) => handleFieldClick(field, e)}
          >
            <span className={`${classPrefix}__custom-field-label`}>
              {field.name.endsWith(':') ? field.name : `${field.name}:`}
            </span>
            
            {isEditing ? (
              <FieldEditor
                field={field}
                fieldType={fieldType}
                value={value}
                onChange={(newValue) => handleValueChange(field.id, newValue)}
                onClose={handleCloseEditor}
              />
            ) : (
              <FieldRenderer
                field={field}
                fieldType={fieldType}
                value={value}
                classPrefix={classPrefix}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default CustomFieldsDisplay;
